// Pure helpers for the Live Clipping ("Clipper") UI.
//
// All app-side times are wall-clock SECONDS (float epoch). The Flussonic DVR
// origin mixes units: ranges.json params/fields are ms epoch, while URL path
// times (index-, archive-, thumbnails) are seconds. Convert at the edges only.

export const DVR_ORIGIN = 'https://castr-highlights.castr.com';

// Quick-clip end must stay this far behind the reported live edge so the
// export never asks for media the origin has not finished writing yet.
export const LIVE_EDGE_SAFETY_SEC = 6;

export function buildDvrBase(userId, streamKey, origin = DVR_ORIGIN) {
	return `${origin}/highlights/${userId}/${streamKey}`;
}

export function rangesUrl(base, fromSec, toSec, cursor) {
	const params = new URLSearchParams({
		closed_at_gte: String(Math.floor(fromSec * 1000)),
		opened_at_lte: String(Math.ceil(toSec * 1000)),
		limit: '100',
	});
	if (cursor) params.set('cursor', cursor);
	return `${base}/ranges.json?${params.toString()}`;
}

export function recordingStatusUrl(base) {
	return `${base}/recording_status.json`;
}

export function mediaInfoUrl(base) {
	return `${base}/media_info.json`;
}

export function livePlaylistUrl(base) {
	return `${base}/index.fmp4.m3u8`;
}

// VOD playlist for one contiguous DVR window. Never span a gap with this: the
// origin inserts EXT-X-DISCONTINUITY without a fresh PDT across gaps.
export function windowPlaylistUrl(base, fromSec, durationSec) {
	const from = Math.floor(fromSec);
	const dur = Math.max(1, Math.ceil(durationSec));
	return `${base}/index-${from}-${dur}.fmp4.m3u8`;
}

export function downloadUrl(base, startSec, endSec) {
	const from = Math.floor(startSec);
	const dur = Math.max(1, Math.ceil(endSec - startSec));
	return `${base}/archive-${from}-${dur}.mp4?packing=compat`;
}

export function thumbnailUrl(base, sec) {
	return `${base}/${Math.floor(sec)}.jpg`;
}

// ranges.json -> [{ start, end }] in seconds, sorted by start.
export function normalizeRanges(apiRanges) {
	if (!Array.isArray(apiRanges)) return [];
	const out = [];
	for (const r of apiRanges) {
		if (!r) continue;
		let start;
		let end;
		if (typeof r.opened_at === 'number' && typeof r.closed_at === 'number') {
			start = r.opened_at / 1000;
			end = r.closed_at / 1000;
		} else if (typeof r.from === 'number' && typeof r.duration === 'number') {
			start = r.from;
			end = r.from + r.duration;
		} else {
			continue;
		}
		if (end > start) out.push({ start, end });
	}
	return out.sort((a, b) => a.start - b.start);
}

// Union of intervals. ranges.json clips results to the query window, so a
// freshly polled live window can return a range whose `start` is the window
// start rather than the real recording start. Coalescing overlapping/touching
// intervals keeps one entry per contiguous recording and lets the live range
// grow in place (newer `end` wins).
export function mergeRanges(existing, incoming, toleranceSec = 0.5) {
	const all = [...(existing || []), ...(incoming || [])]
		.filter((r) => r && r.end > r.start)
		.sort((a, b) => a.start - b.start);
	const out = [];
	for (const r of all) {
		const last = out[out.length - 1];
		if (last && r.start <= last.end + toleranceSec) {
			if (r.end > last.end) last.end = r.end;
		} else {
			out.push({ start: r.start, end: r.end });
		}
	}
	return out;
}

// Drop everything before the DVR retention start (oldest media gets deleted).
export function clipRangesToExtent(ranges, extent) {
	if (!extent || typeof extent.start !== 'number') return ranges;
	const out = [];
	for (const r of ranges) {
		if (r.end <= extent.start) continue;
		out.push({ start: Math.max(r.start, extent.start), end: r.end });
	}
	return out;
}

export function rangeAt(ranges, t) {
	if (typeof t !== 'number' || Number.isNaN(t)) return null;
	for (const r of ranges) {
		if (t >= r.start && t <= r.end) return r;
	}
	return null;
}

export function inRange(ranges, t) {
	return rangeAt(ranges, t) !== null;
}

export function nextRangeStart(ranges, t) {
	for (const r of ranges) {
		if (r.start > t) return r.start;
	}
	return null;
}

export function prevRangeEnd(ranges, t) {
	let best = null;
	for (const r of ranges) {
		if (r.end < t) best = r.end;
	}
	return best;
}

// Clamp [a, b] into the single recorded range that contains the anchor end
// (default: the selection end). Falls back to the range containing the other
// end. Returns null when neither end sits inside a recording or the clamped
// selection collapses.
export function clampSelectionToRange(ranges, a, b, anchor = 'end') {
	if (typeof a !== 'number' || typeof b !== 'number') return null;
	let start = Math.min(a, b);
	let end = Math.max(a, b);
	const anchorPoint = anchor === 'start' ? start : end;
	const otherPoint = anchor === 'start' ? end : start;
	const range = rangeAt(ranges, anchorPoint) || rangeAt(ranges, otherPoint);
	if (!range) return null;
	start = Math.max(start, range.start);
	end = Math.min(end, range.end);
	if (end - start < 0.5) return null;
	return { start, end };
}

// True when the selection lies within one contiguous recording.
export function selectionIsContiguous(ranges, selection) {
	if (!selection) return false;
	const r = rangeAt(ranges, selection.start);
	return !!r && selection.end <= r.end + 0.001;
}

// Gaps (unrecorded stretches) between consecutive recordings.
export function gapsBetween(ranges) {
	const out = [];
	for (let i = 1; i < ranges.length; i += 1) {
		const prevEnd = ranges[i - 1].end;
		const nextStart = ranges[i].start;
		if (nextStart > prevEnd + 0.001) out.push({ start: prevEnd, end: nextStart });
	}
	return out;
}

// Why a selection is not exportable as one file, with the two closest valid
// alternatives. Returns null when the selection sits inside one recording.
//   { gap: {start,end}, before: {start,end}|null, after: {start,end}|null }
// `before` keeps the part of the selection ahead of the gap, `after` the part
// behind it; each is null when that side has no recorded media.
export function selectionGapConflict(ranges, selection) {
	if (!selection || !ranges.length) return null;
	if (selectionIsContiguous(ranges, selection)) return null;
	const { start, end } = selection;
	// Treat time before the first recording / after the last as gaps too.
	const candidates = gapsBetween(ranges);
	const first = ranges[0];
	const last = ranges[ranges.length - 1];
	if (start < first.start) candidates.unshift({ start: -Infinity, end: first.start });
	if (end > last.end) candidates.push({ start: last.end, end: Infinity });
	const gap = candidates.find((g) => start < g.end && end > g.start);
	if (!gap) return null;
	let before = null;
	let after = null;
	if (Number.isFinite(gap.start) && start < gap.start) {
		const r = rangeAt(ranges, gap.start);
		before = { start: Math.max(start, r ? r.start : start), end: gap.start };
		if (before.end - before.start < 1) before = null;
	}
	if (Number.isFinite(gap.end) && end > gap.end) {
		const r = rangeAt(ranges, gap.end);
		after = { start: gap.end, end: Math.min(end, r ? r.end : end) };
		if (after.end - after.start < 1) after = null;
	}
	return { gap, before, after };
}

// --- Export window ----------------------------------------------------------

// Flussonic writes ~6 second fMP4 segments and exports whole segments, so the
// file the origin delivers starts at the segment boundary at or before the
// requested start and ends at the boundary at or after the requested end.
export const SEGMENT_SEC = 6;

// Predict the window the origin will actually export for `selection`.
// `segmentStarts` (sorted wall-clock seconds of known segment starts, e.g.
// from a loaded playlist) gives exact boundaries; otherwise we fall back to a
// 6 second grid anchored at the containing recording's start.
export function snapExportWindow(selection, range, segmentStarts = []) {
	if (!selection) return null;
	const { start, end } = selection;
	const r = range || { start: -Infinity, end: Infinity };
	let expStart = null;
	let expEnd = null;
	if (segmentStarts.length) {
		let below = null;
		let above = null;
		for (const s of segmentStarts) {
			if (s <= start + 1e-6) below = s;
			if (s >= end - 1e-6 && above === null) above = s;
		}
		// Only trust a boundary when it is within one segment of the request;
		// anything further away means the list does not cover that edge.
		if (below !== null && start - below <= SEGMENT_SEC + 0.5) expStart = below;
		if (above !== null && above - end <= SEGMENT_SEC + 0.5) expEnd = above;
	}
	const anchor = Number.isFinite(r.start) ? r.start : Math.floor(start / SEGMENT_SEC) * SEGMENT_SEC;
	if (expStart === null) expStart = anchor + Math.floor((start - anchor) / SEGMENT_SEC) * SEGMENT_SEC;
	if (expEnd === null) expEnd = anchor + Math.ceil((end - anchor) / SEGMENT_SEC) * SEGMENT_SEC;
	expStart = Math.max(expStart, r.start);
	expEnd = Math.min(expEnd, r.end);
	return { start: expStart, end: Math.max(expEnd, expStart + 1) };
}

export function estimateSizeBytes(durationSec, kbps) {
	if (typeof kbps !== 'number' || kbps <= 0 || !(durationSec > 0)) return null;
	return Math.round(durationSec * kbps * 1000 / 8);
}

export function formatBytes(bytes) {
	if (typeof bytes !== 'number' || !(bytes >= 0)) return '';
	if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
	const mb = bytes / (1024 * 1024);
	if (mb < 1024) return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
	return `${(mb / 1024).toFixed(2)} GB`;
}

// File name the origin advertises in Content-Disposition for a download
// (observed: "highlights_{userId}/{streamKey}_{from}_{dur}.mp4"; browsers
// keep the part after the slash).
export function serverFileName(base, startSec, endSec) {
	const from = Math.floor(startSec);
	const dur = Math.max(1, Math.ceil(endSec - startSec));
	const streamKey = String(base || '').split('/').filter(Boolean).pop() || 'clip';
	return `${streamKey}_${from}_${dur}.mp4`;
}

// --- Formatting -----------------------------------------------------------

function pad2(n) {
	return String(n).padStart(2, '0');
}

// 0:07, 1:30, 1:02:03
export function formatClock(sec) {
	const s = Math.max(0, Math.round(sec || 0));
	const h = Math.floor(s / 3600);
	const m = Math.floor((s % 3600) / 60);
	const r = s % 60;
	if (h > 0) return `${h}:${pad2(m)}:${pad2(r)}`;
	return `${m}:${pad2(r)}`;
}

// "30 sec", "1 min", "5 min 30 sec", "1 hr 2 min"
export function formatDurationHuman(sec) {
	const s = Math.max(0, Math.round(sec || 0));
	const h = Math.floor(s / 3600);
	const m = Math.floor((s % 3600) / 60);
	const r = s % 60;
	const parts = [];
	if (h) parts.push(`${h} hr`);
	if (m) parts.push(`${m} min`);
	if (r || parts.length === 0) parts.push(`${r} sec`);
	return parts.slice(0, 2).join(' ');
}

export function formatLocalTime(sec, withSeconds = true) {
	if (typeof sec !== 'number' || Number.isNaN(sec)) return '';
	const d = new Date(sec * 1000);
	const hh = pad2(d.getHours());
	const mm = pad2(d.getMinutes());
	if (!withSeconds) return `${hh}:${mm}`;
	return `${hh}:${mm}:${pad2(d.getSeconds())}`;
}

export function formatLocalDate(sec) {
	if (typeof sec !== 'number' || Number.isNaN(sec)) return '';
	const d = new Date(sec * 1000);
	return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function formatLocalDateTime(sec) {
	if (typeof sec !== 'number' || Number.isNaN(sec)) return '';
	return `${formatLocalDate(sec)} ${formatLocalTime(sec)}`;
}

// Parse "HH:mm:ss" (or "HH:mm") typed by the user into epoch seconds on the
// same local calendar day as `referenceSec`. Picks the day boundary that
// lands closest to the reference so times just past midnight resolve sanely.
export function parseLocalTime(text, referenceSec) {
	if (typeof text !== 'string') return null;
	const m = text.trim().match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
	if (!m) return null;
	const h = Number(m[1]);
	const mi = Number(m[2]);
	const s = m[3] ? Number(m[3]) : 0;
	if (h > 23 || mi > 59 || s > 59) return null;
	const ref = new Date((referenceSec || Date.now() / 1000) * 1000);
	const candidates = [-1, 0, 1].map((dayOffset) => {
		const d = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate() + dayOffset, h, mi, s, 0);
		return d.getTime() / 1000;
	});
	const refSec = ref.getTime() / 1000;
	return candidates.reduce((best, c) => (Math.abs(c - refSec) < Math.abs(best - refSec) ? c : best), candidates[1]);
}

// Tick spacing for the timeline ruler. Returns { major, minor } in seconds
// so that major labels stay at least ~90px apart.
const TICK_STEPS = [
	{ major: 1, minor: 0.5 },
	{ major: 5, minor: 1 },
	{ major: 10, minor: 2 },
	{ major: 15, minor: 5 },
	{ major: 30, minor: 10 },
	{ major: 60, minor: 15 },
	{ major: 300, minor: 60 },
	{ major: 600, minor: 120 },
	{ major: 900, minor: 300 },
	{ major: 1800, minor: 600 },
	{ major: 3600, minor: 900 },
	{ major: 7200, minor: 1800 },
	{ major: 4 * 3600, minor: 3600 },
	{ major: 12 * 3600, minor: 3 * 3600 },
	{ major: 24 * 3600, minor: 6 * 3600 },
];

export function pickTickStep(viewDurationSec, widthPx, minLabelPx = 90) {
	if (!widthPx || !viewDurationSec) return TICK_STEPS[5];
	const secPerPx = viewDurationSec / widthPx;
	const minMajor = secPerPx * minLabelPx;
	for (const step of TICK_STEPS) {
		if (step.major >= minMajor) return step;
	}
	return TICK_STEPS[TICK_STEPS.length - 1];
}

export function clamp(v, lo, hi) {
	return Math.min(hi, Math.max(lo, v));
}

// Suggested file name for downloads/screenshots (no spaces, safe chars).
export function safeFileStem(name, sec) {
	const stem = String(name || 'clip')
		.trim()
		.replace(/[^\w-]+/g, '-')
		.replace(/-+/g, '-')
		.replace(/^-|-$/g, '')
		.slice(0, 60) || 'clip';
	const d = new Date(sec * 1000);
	const stamp = `${d.getFullYear()}${pad2(d.getMonth() + 1)}${pad2(d.getDate())}-${pad2(d.getHours())}${pad2(d.getMinutes())}${pad2(d.getSeconds())}`;
	return `${stem}-${stamp}`;
}
